import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { motion, useInView } from "framer-motion";
import Chevron from "@default/assets/ui/chevron-big.svg";
import PillBorder from "@default/general-components/pill-border";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import classNames from "classnames";

const UniversalSliderButton = ({
  onClick,
  type,
  sliderClassNames,
  sliderRef,
  centerSelector,
  buttonRef,
}) => {
  const animRef = useRef(null);
  const inView = useInView(animRef, {
    once: false,
    margin: "0px 2000px 0px 2000px",
  });
  const { simpleDesign } = useAccessibilityContext();
  const buttonClassNames = classNames({
    "universal-slider-button": true,
    [sliderClassNames]: sliderClassNames,
    [type]: type,
    "basic-centered": !centerSelector,
    "ref-centered": centerSelector,
  });

  useEffect(() => {
    if (!centerSelector || !animRef.current) return;

    const parent = animRef.current.closest(".paragraph");

    if (!parent) return;

    let resizeObserver;

    const applyHeight = (element) => {
      const height = element.getBoundingClientRect().height;

      animRef.current
        ?.closest(".universal-slider-button")
        .style.setProperty("--ref-height", `${height}px`);
    };

    const findElement = () => {
      const element = parent.querySelector(centerSelector);

      if (element && !resizeObserver) {
        resizeObserver = new ResizeObserver(() => applyHeight(element));
        resizeObserver.observe(element);
        mutationObserver.disconnect();
      }
    };

    const mutationObserver = new MutationObserver(findElement);

    mutationObserver.observe(parent, {
      childList: true,
      subtree: true,
    });

    findElement();

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, [centerSelector, sliderRef]);

  const offsetX = type === "next" ? 50 : -50;

  return (
    <button
      type="button"
      onClick={onClick}
      className={buttonClassNames}
      ref={buttonRef}
    >
      <motion.div
        ref={animRef}
        className="icon-wrapper"
        initial={{ x: simpleDesign ? 0 : offsetX }}
        animate={{ x: simpleDesign || inView ? 0 : offsetX }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
      >
        <PillBorder blurredBackdrop />
        {!simpleDesign && <Chevron />}

        {simpleDesign && (
          <FormattedMessage id={type === "prev" ? "back" : "further"} />
        )}

        <span className="sr-only">
          {type === "prev" ? (
            <FormattedMessage id="accessibility.previous_slide" />
          ) : (
            <FormattedMessage id="accessibility.next_slide" />
          )}
        </span>
      </motion.div>
    </button>
  );
};

UniversalSliderButton.propTypes = {
  onClick: PropTypes.func,
  sliderRef: PropTypes.func,
  centerSelector: PropTypes.string,
  type: PropTypes.oneOf(["next", "prev"]).isRequired,
  sliderClassNames: PropTypes.string,
};

export default UniversalSliderButton;
